import { Circle, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import {
  PRESENCE_LABELS,
  heldItemById,
  type DuckColor,
  type DuckSpecies,
  type EquippedCosmetics,
  type PresenceFlavor,
} from '@duck/contracts';
import { isReducedMotion } from './reduced-motion';
import { DuckCosmetics } from './cosmetics';
import { buildCritterArt, SPECIES_SCALE } from './species';

// The per-species body art (the duck + 9 other animals) lives in `species.ts`; this class is the
// species-AGNOSTIC avatar shell around it — shadow, label, speech/presence/reaction layers, the
// cosmetics + held-item mounts, the speaking ring, and the whole `setPose` animation engine. The body
// tint → shade/highlight tones are derived inside the species builders (see `buildPalette`).

// ── M7a "Socialize" timings ──
/** How long a speech bubble holds at full opacity before it begins to fade. */
const SPEECH_HOLD_MS = 4500;
/** How long the bubble takes to fade out after the hold. */
const SPEECH_FADE_MS = 800;
/** Total lifetime of a floating emoji reaction (rise + fade). */
const REACTION_LIFE_MS = 1500;
/** How long a reaction lingers in place before clearing under reduced motion (no float). */
const REACTION_STILL_MS = 1200;
/**
 * Radius (px) of the webcam "show your face" bubble (ADR-0030) — a small round video porthole that
 * floats up-and-to-the-side of the head (the head circle is r≈12 at (14,-14)). Deliberately small and
 * BESIDE the head, never over it: the critter keeps its own face + charm; the real face is a companion.
 */
const WEBCAM_R = 16;
/** Resolution (px, square) of the per-bubble relay canvas the camera frame is drawn into (ADR-0030). */
const WEBCAM_TEX = 96;

/**
 * A single CRITTER avatar (the duck or one of the 9 other animals — ADR-0024) drawn procedurally with
 * Pixi Graphics, no texture atlas (ADR-0010). The species-specific body art comes from `buildCritterArt`
 * (`species.ts`); everything else here is species-agnostic. `root` is positioned in world space by the
 * canvas; the inner `body` handles bob/emote motion so the shadow and label stay put.
 */
export class Critter {
  readonly root = new Container();
  private readonly body = new Container();
  /** Floating name label. Absent for nameless critters (e.g. the entry-screen preview). */
  private readonly label?: Text;
  /** Soft "currently speaking" ring drawn on the floor under the duck (ADR-0009 visible-speaking). */
  private readonly speakingRing: Graphics;
  /**
   * ELECTED presence badge (M4): a small pill above the duck, DUAL-CODED with the flavor's icon +
   * text label (never colour-only). Hidden when the citizen has elected `none`. A chosen flair the
   * citizen displays — never an inferred/scored work-state (anti-surveillance).
   */
  private readonly presenceBadge: Container;
  private readonly presenceBg: Graphics;
  private readonly presenceText: Text;
  private presence: PresenceFlavor = 'none';
  /**
   * Procedural equipped cosmetics (M6 "Express Yourself"). The worn items (hat/glasses/outfit) mount
   * INSIDE `body` so they flip + bob with the duck; the companion pet mounts in `root` so it stays
   * upright on the floor and follows with its own easing. Cosmetic-only — never affects any capability.
   */
  private readonly cosmetics = new DuckCosmetics();
  /** Gentle "z z z" AFK/SLEEPING cue (M4): never punitive — just a soft "resting" tell. */
  private readonly afkCue: Text;
  /**
   * Speech bubble (M7a "Socialize"): a high-contrast rounded pill above the duck that pops in, holds,
   * and fades. A rapid second line REPLACES the first gracefully (re-fits + restarts the timer) rather
   * than stacking. Anchored centrally and re-sized to fit its (word-wrapped) text. NOT in the a11y tree
   * — the React "recent chatter" strip is the screen-reader path (canvas text is invisible to AT).
   */
  private readonly speechBubble: Container;
  private readonly speechBg: Graphics;
  private readonly speechText: Text;
  /** Remaining lifetime (ms) of the current bubble; <=0 hides it. Drives the hold-then-fade. */
  private speechMs = 0;
  /**
   * Floating emoji reactions (M7a): one or more emoji that rise + fade from the duck. Reduced-motion
   * shows them briefly in place (no float). Each tracks its own elapsed time and is pruned when done.
   */
  private readonly reactionLayer: Container;
  private readonly reactions: { text: Text; elapsed: number; driftX: number }[] = [];
  // ── HP bar + on-duck fire (ADR-0052) ──
  /** ❤️ + a thin health track above the head; shown ONLY while hurt (an undamaged duck carries no clutter). */
  private readonly hpBar: Container;
  private readonly hpFill: Graphics;
  /** A small cluster of flame tongues over the body while standing in fire; hidden otherwise. */
  private readonly fireOverlay: Container;
  /** The flame tongues + their phase offsets — setPose flickers scale/alpha over a shared clock (no alloc). */
  private readonly fireFlames: { gfx: Graphics; phase: number }[] = [];
  private burning = false;
  private firePhase = 0;
  /** Last-drawn HP key (quantized ratio, or -1 for "healthy/hidden") so setHp only redraws on a real change. */
  private lastHpKey = -2;
  private afkResting = false;
  private afkPhase = 0;
  private walkPhase = 0;
  private speakPhase = 0;
  private speaking = false;
  /**
   * Live voice LOUDNESS (0..1) while speaking, eased toward {@link speakLevelTarget} each frame. Drives
   * the "shout swell" (the whole head + body grow with volume so a loud voice is bigger across the room),
   * plus how wide the mouth gapes and how bright/large the speaking ring is. Rests at 0 when silent (or
   * under reduced motion), so a quiet duck is always its true size — the swell is purely additive.
   */
  private speakLevel = 0;
  private speakLevelTarget = 0;
  private faceLeft = false;
  /**
   * The hinging MOUTH (the duck's lower mandible, a frog's wide lip, a dino's toothy jaw, …) — split out
   * of the species body so it can HINGE open while talking (voice). The species builder positions it at
   * its mouth-corner pivot; setPose rotates it down by a sine-driven gape while `speaking`, and rests it
   * closed (rotation 0) otherwise. Cheap transform-only animation — no redraw. Built per species below.
   */
  private readonly mouth: Graphics;
  /** Current open-rotation (radians) applied to {@link mouth}; eased toward the target each frame. */
  private mouthGape = 0;
  /**
   * HELD ITEM (Task 2 "HOLD ITEMS"): a fun prop in the duck's hand (guitar, laptop, coffee, …). Mounts
   * INSIDE `body` (in the front "hand" area) so it flips with `body.scale.x` and bobs with the walk,
   * riding the duck. Built procedurally per item id by {@link setHeldItem}; empty when the id is ''.
   * Cosmetic-only — never affects any capability (Constitution Art. 1).
   */
  private readonly heldLayer = new Container();
  /** The held-item id currently shown ('' = empty hands), so re-applying the same prop is a no-op. */
  private heldItem = '';
  /**
   * Per-species uniform body scale (Wave 3 silhouette differentiation, see {@link SPECIES_SCALE}). Folded
   * into the flip in {@link setPose} (`scale.x = ±bodyScale`) so worn cosmetics — children of `body` —
   * scale with it and stay aligned. Duck = 1.0 (the original is never rescaled).
   */
  private readonly bodyScale: number;

  // ── Webcam "show your face" bubble (ADR-0030) — a small live video porthole beside the head ──
  /** The bubble container (backing + masked video sprite + border ring), anchored up-and-side of the head. */
  private readonly webcamBubble: Container;
  /** Warm border ring around the bubble (gold for self, cream for others) — also carries the speaking pulse. */
  private readonly webcamRing: Graphics;
  /** Circular mask that clips the video sprite to a round porthole. */
  private readonly webcamMask: Graphics;
  private webcamSprite: Sprite | null = null;
  /** Pixi texture backing the bubble — backed by {@link webcamCanvas} (NOT the video; see setWebcamStream). */
  private webcamTexture: Texture | null = null;
  private webcamVideo: HTMLVideoElement | null = null;
  /**
   * CANVAS RELAY (ADR-0030). Pixi's VideoSource → WebGL upload renders BLACK for a getUserMedia camera
   * stream (it works for canvas-captureStream, which misled an earlier fix). But `drawImage(video)` reads
   * the camera frame fine, so each visible frame we draw the video into this small canvas and back the Pixi
   * texture with the CANVAS — canvas textures upload reliably. Square buffer; cover-fit is done at drawImage.
   */
  private webcamCanvas: HTMLCanvasElement | null = null;
  private webcamCanvasCtx: CanvasRenderingContext2D | null = null;
  /** The stream currently shown (identity-checked so re-applying the same stream is a cheap no-op). */
  private webcamStream: MediaStream | null = null;
  /** True when this bubble shows the LOCAL player's own camera (gold border, never pinnable as "theirs"). */
  private webcamSelf = false;
  private webcamPhase = 0;
  /**
   * PERF (ADR-0030): the canvas relay (drawImage → texture re-upload) is the per-bubble cost. The camera
   * only delivers ~15-24 fps (see {@link videoConstraints}) but the render loop runs at ~60 fps, so a naive
   * per-frame relay re-uploads each camera frame 3-4× for nothing. We instead repaint ONLY when a NEW
   * camera frame is decoded, driven by `requestVideoFrameCallback`: the callback flips this dirty flag, and
   * setPose does the expensive drawImage+upload only when it's set. Cuts texture uploads ~60-75% with zero
   * change to what's shown. The ring pulse + transform stay per-frame (cheap, must animate smoothly).
   */
  private webcamFrameDirty = true;
  /** True when the browser supports `requestVideoFrameCallback`; false → fall back to the per-frame relay. */
  private webcamUsesRvfc = false;
  /** Outstanding `requestVideoFrameCallback` handle (null = not armed), so we can re-arm / cancel cleanly. */
  private webcamRvfcId: number | null = null;
  /**
   * Tapped-the-bubble callback (engine wires it to pin this peer's camera). Set per-critter by the engine
   * so the click in the world can cross back to the React HUD (ADR-0030). Null = not wired (e.g. preview).
   */
  onWebcamTap: (() => void) | null = null;

  constructor(species: DuckSpecies, color: DuckColor, name: string, isSelf: boolean) {
    // Per-species silhouette scale (Wave 3). Folded into the body flip below; the shadow scales to match
    // so a bigger critter casts a bigger contact shadow (it still sits IN the room, never floating).
    this.bodyScale = SPECIES_SCALE[species] ?? 1;

    // Ground shadow (stays on the floor, not affected by bob). A soft double-ellipse reads as a real
    // contact shadow with a gentle penumbra, so the critter sits IN the room rather than floating on it.
    const shadow = new Graphics()
      .ellipse(0, 23, 23, 8)
      .fill({ color: 0x000000, alpha: 0.12 })
      .ellipse(0, 22, 18, 6)
      .fill({ color: 0x000000, alpha: 0.22 });
    shadow.scale.set(this.bodyScale);

    // Speaking ring — a warm aura under the critter, hidden until they speak. Drawn first so it sits
    // beneath the body. We draw the geometry once and animate via scale/alpha (cheap, no redraw).
    this.speakingRing = new Graphics()
      .ellipse(0, 22, 26, 10)
      .stroke({ color: 0x6fe3a0, width: 3, alpha: 0.9 });
    this.speakingRing.visible = false;

    // The species BODY ART + its hinging MOUTH (the duck, or one of the 9 other animals — ADR-0024),
    // drawn procedurally on the shared anchor rig (body ellipse (0,0) r=(22,18); head circle (14,-14)
    // r=12) so every cosmetic, held item and animation below fits any critter. The body tint →
    // shade/highlight is derived inside the builder; each animal's signature accents are fixed there.
    const art = buildCritterArt(species, color);
    this.mouth = art.mouth;
    this.mouth.position.set(art.mouthPivot.x, art.mouthPivot.y);

    // Body z-order (all INSIDE `body` so they flip with `body.scale.x`, bob with the walk, and scale with
    // the per-species silhouette scale): species BODY → the full-body SKIN overlay (Wave 4 S6, so a skin
    // reskins the head/torso) → the species FACE overlay (eyes/snout, ON TOP of any skin) → the hinging
    // MOUTH → worn cosmetics (hat/glasses/garment, over the whole critter) → the held item (in the hand,
    // out front). With no skin equipped, `skinLayer` is empty and `face` re-stamps the eyes exactly where
    // the body already drew them, so the look is pixel-for-pixel unchanged.
    this.body.addChild(
      art.body,
      this.cosmetics.skinLayer,
      art.face,
      this.mouth,
      this.cosmetics.bodyLayer,
      this.heldLayer,
    );
    // Initial silhouette scale so a still preview (no setPose tick) is already sized; setPose folds the
    // same factor into the flip each frame.
    this.body.scale.set(this.bodyScale);

    // Name label — built only when there's a name. A nameless critter (the entry-screen preview passes
    // '') skips it entirely: an empty label shows nothing, AND destroying an empty Pixi v8 Text trips a
    // texture-pool GC bug (returnTexture on an undefined pool bucket) that would throw on teardown.
    if (name) {
      this.label = new Text({
        text: isSelf ? `${name} (you)` : name,
        style: {
          fill: isSelf ? 0xffe066 : 0xe6e8f0,
          fontSize: 12,
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          fontWeight: '600',
          stroke: { color: 0x0b1020, width: 3 },
          align: 'center',
        },
      });
      this.label.anchor.set(0.5, 1);
      this.label.y = -34;
    }

    // Elected presence badge: a rounded pill (icon + text) above the name. Built once and shown/
    // hidden + relabeled by setPresence. Anchored centrally and re-sized to fit its text.
    this.presenceBadge = new Container();
    this.presenceBg = new Graphics();
    this.presenceText = new Text({
      text: '',
      style: {
        fill: 0x0b1020,
        fontSize: 11,
        fontWeight: '700',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        align: 'center',
      },
    });
    this.presenceText.anchor.set(0.5, 0.5);
    this.presenceBadge.addChild(this.presenceBg, this.presenceText);
    this.presenceBadge.y = -50;
    this.presenceBadge.visible = false;

    // Gentle AFK / resting cue: a soft "z z z" floating above the duck. Never negative — just rest.
    this.afkCue = new Text({
      text: 'z z z',
      style: {
        fill: 0xbcd0ff,
        fontSize: 12,
        fontWeight: '700',
        fontStyle: 'italic',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        stroke: { color: 0x0b1020, width: 3 },
      },
    });
    this.afkCue.anchor.set(0.5, 1);
    this.afkCue.x = 16;
    this.afkCue.y = -36;
    this.afkCue.alpha = 0.85;
    this.afkCue.visible = false;

    // Speech bubble (M7a): a light, high-contrast rounded pill above the name. Built once; shown/
    // relabeled + re-fit by showSpeech, and faded out by the per-frame timer. Word-wrapped so a longer
    // line stays readable without ballooning the world. Sits highest so it never hides behind a badge.
    this.speechBubble = new Container();
    this.speechBg = new Graphics();
    this.speechText = new Text({
      text: '',
      style: {
        fill: 0x0b1020,
        fontSize: 13,
        fontWeight: '600',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        align: 'center',
        wordWrap: true,
        wordWrapWidth: 180,
        breakWords: true,
      },
    });
    this.speechText.anchor.set(0.5, 0.5);
    this.speechBubble.addChild(this.speechBg, this.speechText);
    this.speechBubble.visible = false;

    // Floating reactions (M7a): emoji rise + fade from above the duck. Just a container; each emoji is
    // spawned by showReaction and animated/pruned in setPose.
    this.reactionLayer = new Container();

    // Webcam "show your face" bubble (ADR-0030): a small round video porthole that floats up-and-to-the
    // RIGHT of the head — beside it, never over it, so the critter keeps its own face. Hidden until a
    // stream is attached. Tap-to-pin: it's its own interactive target (a circular hit area) so a click in
    // the world can cross to the HUD. Built once; the video sprite is swapped in/out by setWebcamStream.
    this.webcamBubble = new Container();
    this.webcamBubble.position.set(28, -30);
    this.webcamBubble.visible = false;
    const webcamBacking = new Graphics().circle(0, 0, WEBCAM_R).fill({ color: 0x0b1020, alpha: 0.85 });
    this.webcamMask = new Graphics().circle(0, 0, WEBCAM_R).fill(0xffffff);
    this.webcamRing = new Graphics()
      .circle(0, 0, WEBCAM_R + 1)
      .stroke({ color: 0xfff1cc, width: 2, alpha: 0.95 });
    this.webcamBubble.addChild(webcamBacking, this.webcamMask, this.webcamRing);
    this.webcamBubble.eventMode = 'static';
    this.webcamBubble.cursor = 'pointer';
    this.webcamBubble.hitArea = new Circle(0, 0, WEBCAM_R + 2);
    this.webcamBubble.on('pointertap', () => this.onWebcamTap?.());

    // HP bar (ADR-0052): a ❤️ + a thin health track above the head, shown ONLY while hurt (setHp toggles
    // visibility + redraws the fill). Built once; rides in `root` so it stays upright + level above the duck.
    this.hpBar = new Container();
    this.hpBar.y = -62;
    this.hpBar.visible = false;
    const heart = new Text({ text: '❤️', style: { fontSize: 11 } });
    heart.anchor.set(0.5, 0.5);
    heart.x = -24;
    const hpTrack = new Graphics().roundRect(-15, -4, 32, 6, 3).fill({ color: 0x0b1020, alpha: 0.7 });
    this.hpFill = new Graphics().roundRect(-13, -2, 28, 4, 2).fill(0x4ec06a);
    this.hpBar.addChild(hpTrack, this.hpFill, heart);

    // On-duck FIRE (ADR-0052): a cluster of flame tongues over the body while standing in flames. Built once
    // + hidden; setBurning toggles it and setPose flickers scale/alpha (a STATIC lit frame under reduced
    // motion). Teardrop flames (orange → amber → hot core), each with a phase so they don't pulse in lockstep.
    this.fireOverlay = new Container();
    this.fireOverlay.visible = false;
    const flameSpec: [number, number, number][] = [
      [-7, -4, 0],
      [7, -4, 1.7],
      [0, -16, 3.2],
      [0, -9, 4.6],
    ]; // [offsetX, offsetY, phase]
    for (const [fx, fy, ph] of flameSpec) {
      const flame = new Graphics()
        .moveTo(0, 4)
        .bezierCurveTo(4.5, 0, 3.5, -7, 0, -10)
        .bezierCurveTo(-3.5, -7, -4.5, 0, 0, 4)
        .fill(0xff7a2a)
        .moveTo(0, 3)
        .bezierCurveTo(2.6, 0, 2.2, -4.5, 0, -6.5)
        .bezierCurveTo(-2.2, -4.5, -2.6, 0, 0, 3)
        .fill(0xffc24a)
        .moveTo(0, 1.5)
        .bezierCurveTo(1.3, 0, 1.1, -2.2, 0, -3.6)
        .bezierCurveTo(-1.1, -2.2, -1.3, 0, 0, 1.5)
        .fill(0xfff0b8);
      flame.x = fx;
      flame.y = fy;
      this.fireFlames.push({ gfx: flame, phase: ph });
      this.fireOverlay.addChild(flame);
    }

    // The companion pet (M6) rides in `root` (NOT `body`) so it stays upright on the floor and trails
    // with its own easing. Added before the body so the little companion reads beneath/behind the duck.
    // The webcam bubble sits above the body but below the name/presence/speech overlays so a speech pill
    // always wins (transient + must read), while the bubble stays clear of that vertical column anyway.
    // The fire overlay sits just ABOVE the body (flames engulf the duck); the HP bar joins the top overlays.
    const children: Container[] = [shadow, this.speakingRing, this.cosmetics.petLayer, this.body, this.fireOverlay];
    if (this.label) children.push(this.label);
    children.push(this.webcamBubble, this.presenceBadge, this.afkCue, this.reactionLayer, this.hpBar, this.speechBubble);
    this.root.addChild(...children);
  }

  /**
   * Apply the citizen's EQUIPPED cosmetics (M6). Idempotent + cheap (only rebuilds slots whose style
   * changed), so the engine can drive this every frame straight from the synced look — for SELF AND
   * OTHERS. '' / absent slots clear that piece. The pet's per-frame follow is driven in {@link setPose}.
   */
  setCosmetics(equipped: EquippedCosmetics): void {
    this.cosmetics.apply(equipped);
  }

  /**
   * Show the fun prop the citizen is holding (Task 2 "HOLD ITEMS"), built procedurally per item id and
   * mounted in the hand area (it rides `body`, so it flips + bobs with the duck). '' (or an unknown id)
   * empties the hands. Idempotent + cheap — only rebuilds the prop when the id actually changes, so the
   * caller can drive it every frame straight from the synced `heldItem`. Cosmetic-only, for SELF + OTHERS.
   */
  setHeldItem(id: string): void {
    const safe = heldItemById(id) ? id : '';
    if (safe === this.heldItem) return;
    this.heldItem = safe;
    this.heldLayer.removeChildren().forEach((c) => c.destroy());
    if (safe) this.heldLayer.addChild(buildHeldItem(safe));
  }

  /**
   * Show/hide the ELECTED presence badge (M4). Dual-coded: the flavor's icon + its text label
   * (e.g. "☕ Coffee break"), never colour-only. `'none'` hides it. Idempotent — relays only on a
   * real change so the schema can drive this every frame cheaply.
   */
  setPresence(presence: PresenceFlavor): void {
    if (this.presence === presence) return;
    this.presence = presence;
    if (presence === 'none') {
      this.presenceBadge.visible = false;
      return;
    }
    const { icon, label } = PRESENCE_LABELS[presence];
    this.presenceText.text = `${icon} ${label}`;
    // Re-fit the pill background to the (now-measured) text.
    const padX = 8;
    const padY = 3;
    const w = this.presenceText.width + padX * 2;
    const h = this.presenceText.height + padY * 2;
    this.presenceBg
      .clear()
      .roundRect(-w / 2, -h / 2, w, h, h / 2)
      .fill({ color: 0xffe066, alpha: 0.95 })
      .stroke({ color: 0x0b1020, width: 1.5, alpha: 0.6 });
    this.presenceBadge.visible = true;
  }

  /**
   * Toggle the gentle AFK/SLEEPING resting cue (M4). Reduced-motion safe (steady, no float). Never
   * punitive — driven by the movement-derived `activity`, shown as a soft "resting" tell, not a score.
   */
  setResting(resting: boolean): void {
    if (this.afkResting === resting) return;
    this.afkResting = resting;
    this.afkCue.visible = resting;
    if (!resting) {
      this.afkCue.y = -36;
      this.afkCue.alpha = 0.85;
    }
  }

  /**
   * Pop a speech bubble over the duck (M7a "Socialize"). A rapid second line REPLACES the first
   * gracefully: we relabel, re-fit the pill, and restart the hold-then-fade timer (no stacking). The
   * bubble holds for ~4.5s then fades over the last ~0.8s (driven in {@link setPose}). Empty/blank text
   * is ignored. Anchored above the name + badge so it reads clearly without blocking the world.
   */
  showSpeech(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.speechText.text = trimmed;
    // Re-fit the pill background to the (now-measured, possibly wrapped) text.
    const padX = 10;
    const padY = 6;
    const w = this.speechText.width + padX * 2;
    const h = this.speechText.height + padY * 2;
    this.speechBg
      .clear()
      .roundRect(-w / 2, -h / 2, w, h, 10)
      .fill({ color: 0xffffff, alpha: 0.96 })
      .stroke({ color: 0x0b1020, width: 1.5, alpha: 0.7 });
    // Sit the bubble above the name/badge, scaling its lift with the (wrapped) height.
    this.speechBubble.y = -64 - h / 2;
    this.speechBubble.visible = true;
    this.speechBubble.alpha = 1;
    this.speechMs = SPEECH_HOLD_MS + SPEECH_FADE_MS; // restart the hold-then-fade
  }

  /**
   * Float an emoji reaction up from the duck (M7a). Spawns one rising, fading emoji; multiple in quick
   * succession spread out and animate independently. Under reduced motion it appears briefly in place
   * (no float), then clears — the information (which emoji) is identical either way.
   */
  showReaction(reaction: string): void {
    const text = new Text({
      text: reaction,
      style: { fontSize: 26, fontFamily: 'ui-sans-serif, system-ui, sans-serif', align: 'center' },
    });
    text.anchor.set(0.5, 1);
    text.y = -52;
    // A small deterministic-ish horizontal drift so simultaneous reactions don't perfectly overlap.
    text.x = (this.reactions.length % 3) * 10 - 10;
    this.reactionLayer.addChild(text);
    this.reactions.push({ text, elapsed: 0, driftX: text.x });
  }

  /** Toggle the visible "speaking" cue. Driven by the server's active-speaker set via the engine. */
  setSpeaking(speaking: boolean): void {
    if (this.speaking === speaking) return;
    this.speaking = speaking;
    this.speakingRing.visible = speaking;
    if (!speaking) {
      this.speakingRing.scale.set(1);
      this.speakingRing.alpha = 0.9;
      this.speakLevelTarget = 0; // fall silent ⇒ deflate back to true size (eased in setPose)
    }
  }

  /**
   * Set this citizen's live voice LOUDNESS (0..1) while they speak — louder ⇒ the duck SWELLS bigger so
   * a shout is unmistakable across the room (the value is eased toward in {@link setPose}, and the swell
   * only applies while `speaking` + motion is allowed). The engine pushes 0 when they fall silent. Cheap:
   * just stamps the target; all the easing/transform happens in the per-frame pose.
   */
  setSpeakLevel(level: number): void {
    this.speakLevelTarget = level > 0 ? (level < 1 ? level : 1) : 0;
  }

  /**
   * Attach (or clear) this critter's webcam stream — the small face bubble beside the head (ADR-0030).
   * Idempotent: re-passing the same stream is a cheap no-op (it just ensures the video keeps decoding,
   * which matters after a cull pause). A null stream tears the bubble down. `isSelf` tints the border
   * gold (the "(you)" colour) so the player always knows which bubble is their own outgoing face.
   */
  setWebcamStream(stream: MediaStream | null, isSelf: boolean): void {
    if (stream === this.webcamStream && isSelf === this.webcamSelf) {
      // Same stream — resume decode if it was paused while culled off-screen (ADR-0025). Repaint the first
      // available frame at once (don't wait for the next rVFC tick) and re-arm the frame callback.
      if (stream && this.webcamVideo?.paused) {
        void this.webcamVideo.play().catch(() => undefined);
        this.webcamFrameDirty = true;
        this.armWebcamFrame();
      }
      return;
    }
    this.webcamSelf = isSelf;

    if (!stream) {
      // HIDE, don't destroy: pause + detach the stream but KEEP the video element + VideoSource + sprite
      // for reuse. Destroying the source on every off would race the source's initial async load() — its
      // metadata callback reads `resource.videoWidth` and would hit a nulled resource (uncaught error).
      // We tear the source down for real only once, in destroy() (the critter leaving), long after load().
      this.webcamStream = null;
      this.webcamBubble.visible = false;
      this.cancelWebcamFrame();
      if (this.webcamVideo) {
        try {
          this.webcamVideo.pause();
        } catch {
          /* already paused */
        }
        this.webcamVideo.srcObject = null; // stream tracks are owned + stopped by the voice client
      }
      return;
    }

    // Lazily build the REUSABLE off-DOM video element + a CANVAS-backed texture the FIRST time only. Pixi's
    // VideoSource renders BLACK for a getUserMedia camera stream (works for canvas-captureStream, which
    // misled an earlier fix), but `drawImage(video)` reads the camera frame fine — so we relay video →
    // canvas → texture each frame in setPose, and a canvas texture uploads reliably.
    if (!this.webcamVideo) {
      const video = document.createElement('video');
      video.muted = true; // audio rides the spatial voice graph, never this element
      video.autoplay = true;
      video.playsInline = true;
      this.webcamVideo = video;
      // Repaint the relay only on a freshly-decoded frame where the browser supports it (~all modern ones);
      // otherwise fall back to the per-frame relay so the bubble still updates everywhere (no regression).
      this.webcamUsesRvfc = typeof video.requestVideoFrameCallback === 'function';
      const canvas = document.createElement('canvas');
      canvas.width = WEBCAM_TEX;
      canvas.height = WEBCAM_TEX;
      this.webcamCanvas = canvas;
      this.webcamCanvasCtx = canvas.getContext('2d');
      const texture = Texture.from(canvas);
      this.webcamTexture = texture;
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.mask = this.webcamMask;
      // Square canvas → fill the porthole diameter. Constant (WEBCAM_R + WEBCAM_TEX never change), so set
      // once here rather than every frame in setPose.
      sprite.scale.set((WEBCAM_R * 2) / canvas.width);
      this.webcamSprite = sprite;
      // Insert above the backing (index 0), below the mask + ring, so the ring frames it on top.
      this.webcamBubble.addChildAt(sprite, 1);
    }

    // Swap in the (new) stream on the reused element and play. Mark dirty + arm the frame callback so the
    // first decoded frame paints promptly.
    this.webcamStream = stream;
    this.webcamVideo.srcObject = stream;
    this.webcamFrameDirty = true;
    void this.webcamVideo.play().catch(() => undefined);
    this.armWebcamFrame();

    // Warm border: gold for my own face (matches the "(you)" label), cream for others.
    this.webcamRing
      .clear()
      .circle(0, 0, WEBCAM_R + 1)
      .stroke({ color: isSelf ? 0xffe066 : 0xfff1cc, width: isSelf ? 2.5 : 2, alpha: 0.95 });
    this.webcamBubble.visible = true;
  }

  /**
   * Arm a one-shot `requestVideoFrameCallback`: it fires once per freshly-decoded camera frame (~15-24 fps),
   * flags the relay dirty, and re-arms itself — so the expensive drawImage+upload in setPose runs at the
   * CAMERA's rate, not the 60 fps render rate. Idempotent (the handle guard ignores a double-arm); a no-op
   * when the browser lacks rVFC (the per-frame fallback covers that case).
   */
  private armWebcamFrame(): void {
    const v = this.webcamVideo;
    if (!v || !this.webcamUsesRvfc || this.webcamRvfcId !== null) return;
    this.webcamRvfcId = v.requestVideoFrameCallback(this.onWebcamFrame);
  }

  private readonly onWebcamFrame = (): void => {
    this.webcamRvfcId = null;
    this.webcamFrameDirty = true;
    this.armWebcamFrame(); // chain to the next decoded frame
  };

  /** Cancel any outstanding frame callback (bubble hidden / video torn down). */
  private cancelWebcamFrame(): void {
    const v = this.webcamVideo;
    if (v && this.webcamRvfcId !== null) {
      try {
        v.cancelVideoFrameCallback?.(this.webcamRvfcId);
      } catch {
        /* element may be mid-teardown */
      }
    }
    this.webcamRvfcId = null;
  }

  /**
   * Dev/e2e diagnostic (ADR-0030): the live render state of this critter's webcam bubble. `painted` SAMPLES
   * the relay canvas for non-black pixels — the crucial check, since the bubble can be visible with a sized
   * texture yet render BLACK (the bug where the camera frame never reaches the GPU). A data-only or
   * dimensions-only check misses that; `painted` catches it.
   */
  webcamRenderState(): {
    visible: boolean;
    readyState: number;
    texW: number;
    texH: number;
    paused: boolean;
    painted: boolean;
  } {
    let painted = false;
    const cv = this.webcamCanvas;
    const ctx = this.webcamCanvasCtx;
    if (cv && ctx && this.webcamBubble.visible) {
      try {
        const d = ctx.getImageData(Math.floor(cv.width / 2) - 4, Math.floor(cv.height / 2) - 4, 8, 8).data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i]! + d[i + 1]! + d[i + 2]! > 12) {
            painted = true;
            break;
          }
        }
      } catch {
        /* canvas may be transiently unreadable */
      }
    }
    return {
      visible: this.webcamBubble.visible,
      readyState: this.webcamVideo?.readyState ?? -1,
      texW: this.webcamTexture?.width ?? 0,
      texH: this.webcamTexture?.height ?? 0,
      paused: this.webcamVideo?.paused ?? true,
      painted,
    };
  }

  /** Pause webcam decode when the avatar is culled off-screen / into another room (ADR-0025 + 0030). */
  pauseWebcam(): void {
    if (this.webcamVideo && !this.webcamVideo.paused) {
      try {
        this.webcamVideo.pause();
      } catch {
        /* element may be mid-teardown */
      }
    }
  }

  /**
   * Tear down the bubble's video element + canvas texture FOR REAL (the critter is leaving — NOT the
   * MediaStream, which the voice client owns). On a mere camera toggle we HIDE instead (setWebcamStream),
   * so the texture/video are reused. A canvas-backed texture has no async load, so destroy is race-free.
   */
  private teardownWebcam(): void {
    if (this.webcamSprite) {
      this.webcamSprite.mask = null;
      this.webcamSprite.destroy();
      this.webcamSprite = null;
    }
    if (this.webcamVideo) {
      this.cancelWebcamFrame();
      try {
        this.webcamVideo.pause();
      } catch {
        /* already paused */
      }
      this.webcamVideo.srcObject = null; // detach the stream; tracks are owned + stopped by voice-client
      this.webcamVideo = null;
    }
    if (this.webcamTexture) {
      this.webcamTexture.destroy(true);
      this.webcamTexture = null;
    }
    this.webcamCanvas = null;
    this.webcamCanvasCtx = null;
    this.webcamStream = null;
    this.webcamBubble.visible = false;
  }

  /**
   * Update the HP bar (ADR-0052). Hidden at full health (no clutter on a healthy duck); the fill shrinks
   * and shifts green → amber → red as health drops. Idempotent + cheap (one redraw only while hurt).
   */
  setHp(hp: number, max: number): void {
    const ratio = max > 0 ? Math.max(0, Math.min(1, hp / max)) : 1;
    const hurt = ratio < 0.999;
    // Idempotent (mirrors setBurning's no-change guard): the engine calls this every frame, so only touch
    // Pixi when the QUANTIZED ratio (or the hurt state) actually changes — no per-frame clear()+redraw.
    const key = hurt ? Math.round(ratio * 64) : -1;
    if (key === this.lastHpKey) return;
    this.lastHpKey = key;
    this.hpBar.visible = hurt;
    if (!hurt) return;
    const col = ratio > 0.5 ? 0x4ec06a : ratio > 0.25 ? 0xffc24a : 0xff5d5d;
    this.hpFill.clear().roundRect(-13, -2, Math.max(1, 28 * ratio), 4, 2).fill(col);
  }

  /** Toggle the on-duck fire overlay (ADR-0052). Cheap idempotent flag; setPose flickers it while on. */
  setBurning(on: boolean): void {
    if (this.burning === on) return;
    this.burning = on;
    this.fireOverlay.visible = on;
  }

  /**
   * @param facing   radians; used to flip the sprite left/right
   * @param moving   whether to play the walk bob
   * @param animation current animation state (drives emote motion)
   * @param dtMs      frame delta in ms
   */
  setPose(
    x: number,
    y: number,
    facing: number,
    moving: boolean,
    animation: string,
    dtMs: number,
  ): void {
    this.root.x = x;
    this.root.y = y;

    // Flip horizontally based on facing, with a dead-zone so near-vertical motion doesn't flicker. The
    // per-species silhouette scale (Wave 3) is folded into both axes so cosmetics (children of body) ride
    // along; the flip only negates x.
    const cos = Math.cos(facing);
    if (cos < -0.2) this.faceLeft = true;
    else if (cos > 0.2) this.faceLeft = false;
    this.body.scale.x = (this.faceLeft ? -1 : 1) * this.bodyScale;
    this.body.scale.y = this.bodyScale;

    if (moving) {
      this.walkPhase += dtMs * 0.018;
      this.body.y = -Math.abs(Math.sin(this.walkPhase)) * 5;
      this.body.rotation = Math.sin(this.walkPhase) * 0.05;
    } else if (animation === 'dance') {
      this.walkPhase += dtMs * 0.02;
      this.body.y = -Math.abs(Math.sin(this.walkPhase * 2)) * 7;
      this.body.rotation = Math.sin(this.walkPhase) * 0.25;
    } else if (animation === 'wave' || animation === 'laugh') {
      this.walkPhase += dtMs * 0.012;
      this.body.y = 0;
      this.body.rotation = Math.sin(this.walkPhase * 2) * 0.12;
    } else if (animation === 'spin') {
      // 🌀 Tornado: a continuous full-body whirl with little hops. Reduced-motion swaps the dizzy spin
      // for a gentle wiggle so it never induces nausea.
      if (isReducedMotion()) {
        this.walkPhase += dtMs * 0.012;
        this.body.y = 0;
        this.body.rotation = Math.sin(this.walkPhase) * 0.15;
      } else {
        this.walkPhase += dtMs * 0.012;
        this.body.rotation = this.walkPhase; // ever-increasing → full 360° rotation
        this.body.y = -Math.abs(Math.sin(this.walkPhase)) * 3;
      }
    } else if (animation === 'jump') {
      // 🦘 Big hype leaps — tall vertical bounce with a tiny in-air tilt. Reduced-motion = small hops.
      const amp = isReducedMotion() ? 4 : 18;
      this.walkPhase += dtMs * 0.014;
      this.body.y = -Math.abs(Math.sin(this.walkPhase)) * amp;
      this.body.rotation = isReducedMotion() ? 0 : Math.sin(this.walkPhase * 2) * 0.06;
    } else if (animation === 'panic') {
      // 😱 Freak-out — frantic jitter from two mismatched frequencies so it reads as pure chaos.
      // Reduced-motion calms it to a small shiver.
      if (isReducedMotion()) {
        this.walkPhase += dtMs * 0.02;
        this.body.y = Math.sin(this.walkPhase * 3) * 1;
        this.body.rotation = Math.sin(this.walkPhase * 5) * 0.06;
      } else {
        this.walkPhase += dtMs * 0.04;
        this.body.y = Math.sin(this.walkPhase * 3) * 4;
        this.body.rotation = Math.sin(this.walkPhase * 5) * 0.3;
      }
    } else if (animation === 'headbang') {
      // 🤘 Metal nod — a fast forward head-dip (rotate forward + dip down together). Reduced = soft nod.
      const intensity = isReducedMotion() ? 0.1 : 1;
      this.walkPhase += dtMs * (isReducedMotion() ? 0.012 : 0.03);
      const nod = Math.abs(Math.sin(this.walkPhase));
      this.body.rotation = -nod * 0.5 * intensity;
      this.body.y = nod * 4 * intensity;
    } else if (animation === 'wobble') {
      // 🥴 Woozy — a wide, slow drunken sway. Reduced-motion narrows the lean so it stays comfortable.
      const amp = isReducedMotion() ? 0.1 : 0.45;
      this.walkPhase += dtMs * 0.006;
      this.body.rotation = Math.sin(this.walkPhase) * amp;
      this.body.y = Math.sin(this.walkPhase * 0.5) * 2;
    } else if (animation === 'vibe') {
      // 🪩 Groove — a hard side-to-side floss swing with a light bob, out of phase for a hip-shaking read.
      const amp = isReducedMotion() ? 0.12 : 0.35;
      this.walkPhase += dtMs * 0.018;
      this.body.rotation = Math.sin(this.walkPhase) * amp;
      this.body.y = isReducedMotion() ? 0 : -Math.abs(Math.sin(this.walkPhase * 2)) * 4;
    } else if (animation === 'type') {
      // Workstation: a quick, small vertical bob — busy finger-typing. Steady when reduced-motion.
      if (isReducedMotion()) {
        this.body.y = 0;
        this.body.rotation = 0;
      } else {
        this.walkPhase += dtMs * 0.03;
        this.body.y = -Math.abs(Math.sin(this.walkPhase * 2)) * 2;
        this.body.rotation = 0;
      }
    } else if (animation === 'coffee') {
      // Coffee machine: a slow sip lean — a gentle vertical dip + slight rotation. Steady when reduced.
      if (isReducedMotion()) {
        this.body.y = 0;
        this.body.rotation = 0;
      } else {
        this.walkPhase += dtMs * 0.005;
        const t = (Math.sin(this.walkPhase) + 1) / 2; // 0..1
        this.body.y = t * 2.5;
        this.body.rotation = t * 0.1;
      }
    } else if (animation === 'sit') {
      // Seated (Task 2 / sofa): the duck reads as SITTING ON the seat — settled a touch LOWER + centred
      // (a small downward offset tucks the legs onto the seat), with a low-amplitude relaxed bob. Steady
      // when reduced-motion. The server snaps the duck's world position onto the seat anchor, so this is
      // purely the resting POSE on top of that (no client position hacks).
      const SIT_DROP = 4; // sit a little lower so the body settles onto the seat
      if (isReducedMotion()) {
        this.body.y = SIT_DROP;
        this.body.rotation = 0;
      } else {
        this.walkPhase += dtMs * 0.0025;
        this.body.y = SIT_DROP + Math.sin(this.walkPhase) * 0.8;
        this.body.rotation = 0;
      }
    } else {
      // Idle breathing — gated like every other emote so resting ducks stay still under reduced-motion.
      if (isReducedMotion()) {
        this.body.y = 0;
        this.body.rotation = 0;
      } else {
        this.walkPhase += dtMs * 0.004;
        this.body.y = Math.sin(this.walkPhase) * 1.2;
        this.body.rotation = 0;
      }
    }

    // ── Shout swell (voice loudness) ──────────────────────────────────────────────────────────────
    // The whole head + body SWELL with how loudly the citizen is speaking, so a louder voice is
    // physically bigger across the room ("the loudest duck looms largest"). The swell MULTIPLIES the
    // per-species silhouette scale (bodyScale, set just above) and grows UP from the feet (ground-
    // compensated) so it reads as the duck rising to shout, never sinking through the floor. Reduced-
    // motion holds it at true size — a volume-tracking scale is motion, so we never impose it; the
    // static open-mouth + steady ring still convey "speaking" for those players.
    const swellTarget = this.speaking && !isReducedMotion() ? this.speakLevelTarget : 0;
    // Asymmetric ease: swell UP fast so a shout pops, settle back a touch softer so it doesn't snap.
    const swellK = swellTarget > this.speakLevel ? 0.03 : 0.012;
    this.speakLevel += (swellTarget - this.speakLevel) * Math.min(1, dtMs * swellK);
    if (Math.abs(swellTarget - this.speakLevel) < 0.004) this.speakLevel = swellTarget;
    if (this.speakLevel > 0.001) {
      const SPEAK_MAX_INFLATE = 0.2; // +20% width at a full-throated shout — bold but not grotesque
      const SPEAK_MAX_STRETCH = 0.28; // a touch more height so the duck "rises up" as it gets louder
      const FEET_Y = 23; // body-local ground contact (≈ shadow); keep it planted while the body grows
      const sx = 1 + this.speakLevel * SPEAK_MAX_INFLATE;
      const sy = 1 + this.speakLevel * SPEAK_MAX_STRETCH;
      // Compose with the facing flip + the per-species bodyScale already applied above.
      this.body.scale.x = (this.faceLeft ? -1 : 1) * this.bodyScale * sx;
      this.body.scale.y = this.bodyScale * sy;
      this.body.y -= (sy - 1) * FEET_Y * this.bodyScale; // grow upward from the feet, not through the floor
    }

    // Speaking ring: a soft pulse while speaking (steady ring if reduced-motion is requested). A louder
    // voice throws a BIGGER, brighter ring on the floor, so the shout's footprint reads across the room.
    if (this.speaking && !isReducedMotion()) {
      this.speakPhase += dtMs * 0.006;
      const t = (Math.sin(this.speakPhase) + 1) / 2; // 0..1
      const loud = this.speakLevel;
      this.speakingRing.scale.set(1 + loud * 0.35 + t * (0.25 + loud * 0.25));
      this.speakingRing.alpha = Math.min(1, 0.45 + loud * 0.25 + t * 0.5);
    }

    // Talking mouth: the hinging mouth (beak / lip / jaw, per species) gapes open + closed while speaking,
    // hinged at its mouth corner. A faster sine than the ring reads as little "yap yap" movements.
    // Reduced-motion shows a single static OPEN mouth instead (the cue is still visible, just without the
    // rhythm). We ease the gape toward its target so it opens/closes softly and never snaps when toggling.
    const MAX_GAPE = 0.5; // radians — the fully-open swing of the hinging mouth
    let targetGape = 0;
    if (this.speaking) {
      if (isReducedMotion()) {
        targetGape = MAX_GAPE * 0.6; // a gentle, steady "open" cue
      } else {
        // Reuse the ring's phase (advanced above) at ~2× for a livelier mouth; bias open so it gapes
        // more than it fully closes, which reads as natural chatter rather than a clamping snap. The
        // gape also widens with LOUDNESS — quiet talk barely parts the mouth, a shout throws it wide.
        const g = (Math.sin(this.speakPhase * 2) + 1) / 2; // 0..1
        const loudScale = 0.5 + this.speakLevel * 0.5; // 0.5 (murmur) → 1.0 (full shout)
        targetGape = MAX_GAPE * (0.25 + g * 0.75) * loudScale;
      }
    }
    // Cheap critically-damped-ish ease toward the target (frame-rate aware), applied as a transform only.
    if (this.mouthGape !== targetGape) {
      const ease = Math.min(1, dtMs * 0.02);
      this.mouthGape += (targetGape - this.mouthGape) * ease;
      if (Math.abs(this.mouthGape - targetGape) < 0.002) this.mouthGape = targetGape;
      this.mouth.rotation = this.mouthGape;
    }

    // AFK "z z z": a slow, gentle bob + fade while resting (steady under reduced-motion). Soft, never
    // alarming — the world is just letting the duck nap.
    if (this.afkResting && !isReducedMotion()) {
      this.afkPhase += dtMs * 0.003;
      const t = (Math.sin(this.afkPhase) + 1) / 2; // 0..1
      this.afkCue.y = -36 - t * 4;
      this.afkCue.alpha = 0.55 + t * 0.4;
    }

    // Speech bubble (M7a): hold then fade. The pill counts down its lifetime; the last SPEECH_FADE_MS
    // eases the alpha to 0, then it hides. No motion (it only fades), so it's reduced-motion safe as-is.
    if (this.speechMs > 0) {
      this.speechMs -= dtMs;
      if (this.speechMs <= 0) {
        this.speechBubble.visible = false;
        this.speechBubble.alpha = 1;
      } else if (this.speechMs < SPEECH_FADE_MS) {
        this.speechBubble.alpha = this.speechMs / SPEECH_FADE_MS;
      }
    }

    // Floating reactions (M7a): rise + fade (steady, fade-only under reduced motion), pruned when done.
    if (this.reactions.length > 0) {
      const reduce = isReducedMotion();
      for (let i = this.reactions.length - 1; i >= 0; i--) {
        const r = this.reactions[i]!;
        r.elapsed += dtMs;
        if (reduce) {
          // Brief, motionless show: hold, then a quick fade at the tail.
          const fade = r.elapsed > REACTION_STILL_MS ? Math.max(0, 1 - (r.elapsed - REACTION_STILL_MS) / 200) : 1;
          r.text.alpha = fade;
          if (r.elapsed >= REACTION_STILL_MS + 200) {
            r.text.destroy();
            this.reactions.splice(i, 1);
          }
        } else {
          const t = r.elapsed / REACTION_LIFE_MS; // 0..1
          r.text.y = -52 - t * 34; // rise
          r.text.alpha = Math.max(0, 1 - t * t); // fade (ease-out)
          if (r.elapsed >= REACTION_LIFE_MS) {
            r.text.destroy();
            this.reactions.splice(i, 1);
          }
        }
      }
    }

    // Webcam bubble (ADR-0030): relay the live camera frame into the canvas texture, and pulse the border
    // while the owner is speaking. Reduced motion keeps a steady, clearly-lit border (the "this face is
    // speaking" cue survives without the rhythm — dual-coded alongside the floor ring + mouth gape).
    if (
      this.webcamBubble.visible &&
      this.webcamSprite &&
      this.webcamTexture &&
      this.webcamVideo &&
      this.webcamCanvas &&
      this.webcamCanvasCtx
    ) {
      const v = this.webcamVideo;
      // Relay (drawImage + GPU re-upload) ONLY when there's a fresh frame: rVFC flags dirty at the camera's
      // ~15-24 fps; without rVFC we fall back to every frame. This is the dominant per-bubble cost, so gating
      // it cuts uploads ~60-75% with zero change to what's shown (PERF, ADR-0030).
      const needsRelay = !this.webcamUsesRvfc || this.webcamFrameDirty;
      if (needsRelay && v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
        // Cover-fit at draw time: crop the centered SQUARE of the camera frame into the square canvas, then
        // re-upload the canvas texture. (drawImage reads the camera frame even though Pixi's VideoSource
        // can't upload it.) The mask then clips the square to the round porthole.
        const side = Math.min(v.videoWidth, v.videoHeight);
        const sx = (v.videoWidth - side) / 2;
        const sy = (v.videoHeight - side) / 2;
        try {
          this.webcamCanvasCtx.drawImage(v, sx, sy, side, side, 0, 0, this.webcamCanvas.width, this.webcamCanvas.height);
          this.webcamTexture.source.update();
          this.webcamFrameDirty = false; // consumed; rVFC re-flags it on the next decoded frame
          // Dev/e2e/perf counter: total relay uploads this session (proves the per-frame → per-camera-frame
          // reduction; see e2e/perf-webcam-uploads.mjs). One integer add, dwarfed by the upload it counts.
          const g = globalThis as { __duckWebcamUploads?: number };
          g.__duckWebcamUploads = (g.__duckWebcamUploads ?? 0) + 1;
        } catch {
          /* a frame can be transiently unreadable; next frame retries */
        }
      }
      if (this.speaking && !isReducedMotion()) {
        this.webcamPhase += dtMs * 0.006;
        const t = (Math.sin(this.webcamPhase) + 1) / 2; // 0..1
        this.webcamRing.scale.set(1 + t * 0.12);
        this.webcamRing.alpha = 0.7 + t * 0.3;
      } else {
        this.webcamRing.scale.set(1);
        this.webcamRing.alpha = this.speaking ? 1 : 0.95; // steady-lit when speaking under reduced motion
      }
    }

    // On-duck FIRE flicker (ADR-0052): a cheap alpha/scale wobble over a shared phase (no allocation), and
    // a STATIC lit frame under reduced motion. Runs only while burning (the overlay is hidden otherwise).
    if (this.burning) {
      if (isReducedMotion()) {
        for (const fl of this.fireFlames) {
          fl.gfx.alpha = 0.9;
          fl.gfx.scale.set(1);
        }
      } else {
        this.firePhase += dtMs * 0.011;
        for (const fl of this.fireFlames) {
          const s = 1 + Math.sin(this.firePhase + fl.phase) * 0.22;
          fl.gfx.scale.set(0.95 + (s - 1) * 0.4, s);
          fl.gfx.alpha = 0.78 + Math.sin(this.firePhase * 1.4 + fl.phase * 1.3) * 0.22;
        }
      }
    }

    // Companion pet (M6): trails the duck with gentle easing (still follows under reduced-motion, just
    // without the idle bounce). Driven off the same facing the body flips on.
    this.cosmetics.updatePet(facing, dtMs);
  }

  destroy(): void {
    // Tear down the webcam video element + texture FIRST — they're plain DOM/GPU resources, not Pixi
    // children, so the recursive root.destroy below won't reach them.
    this.teardownWebcam();
    // Every cosmetic node (worn layers + pet) is parented under `root`, so the recursive destroy below
    // tears them all down — no separate cosmetics.destroy() needed (and avoids a double-destroy).
    this.root.destroy({ children: true });
  }
}

// ── Held items (Task 2 "HOLD ITEMS") ─────────────────────────────────────────────────────────────────
//
// Procedural props a duck holds in hand, drawn with Pixi primitives ONLY (no texture assets, per
// ADR-0010). The builder switches on the catalog item id and returns a small Graphics authored in the
// duck's BODY-local space (body ellipse r≈(22,18) at (0,0), drawn facing RIGHT). The "hand" the prop
// sits in is the front of the body, around (16, 8); items lean out front so they read as held. The owning
// Duck mounts this inside `body`, so the prop flips with `body.scale.x` and bobs with the walk. Cosmetic-
// only — never a capability (Constitution Art. 1). The id is validated upstream against `HELD_ITEMS`.

const HELD_WOOD = 0x9c6b3f;
const HELD_DARK = 0x2a2f3a;
const HELD_WHITE = 0xf4f4f8;

/** Build the procedural Graphics for a held item by its catalog id (front-of-body, facing right). */
function buildHeldItem(id: string): Graphics {
  const g = new Graphics();
  // Hand anchor (front of the body). Most props lean up-and-out from here.
  const hx = 16;
  const hy = 8;
  switch (id) {
    case 'guitar': {
      // A little acoustic guitar held across the front: a body, a sound hole, a neck angling up + strings.
      g.ellipse(hx + 2, hy + 4, 9, 11).fill(HELD_WOOD); // lower bout
      g.ellipse(hx + 2, hy - 4, 6, 7).fill(HELD_WOOD); // upper bout
      g.circle(hx + 2, hy + 2, 3).fill(0x3a2a18); // sound hole
      g.roundRect(hx - 1, hy - 18, 3, 16, 1).fill(0x5e3d22); // neck up-left
      g.roundRect(hx - 2, hy - 21, 5, 4, 1).fill(HELD_DARK); // headstock
      g.moveTo(hx + 1, hy + 12).lineTo(hx + 1, hy - 17).stroke({ color: 0xe8e8ee, width: 0.6, alpha: 0.7 }); // strings
      break;
    }
    case 'laptop': {
      // An open laptop balanced on both hands: a glowing screen + a keyboard base.
      g.roundRect(hx - 8, hy + 4, 22, 4, 1).fill(0x6b7382); // base
      g.roundRect(hx - 7, hy + 4.5, 20, 1.4, 0.7).fill(0x4a4f5a); // keys hint
      g.roundRect(hx - 6, hy - 9, 18, 13, 1.5).fill(HELD_DARK); // lid
      g.roundRect(hx - 4.5, hy - 7.5, 15, 10, 1).fill(0x2bd1ff); // screen glow
      g.roundRect(hx - 4.5, hy - 7.5, 15, 3, 1).fill({ color: HELD_WHITE, alpha: 0.25 }); // screen sheen
      break;
    }
    case 'coffee': {
      // A steaming coffee cup: a mug, a handle, and two little rising steam curls.
      g.roundRect(hx - 4, hy - 2, 11, 12, 2).fill(HELD_WHITE);
      g.roundRect(hx - 4, hy - 2, 11, 3.5, 2).fill(0x6f4a2f); // coffee surface
      g.circle(hx + 8, hy + 4, 3).stroke({ color: HELD_WHITE, width: 2 }); // handle
      g.moveTo(hx - 1, hy - 4).bezierCurveTo(hx - 3, hy - 8, hx + 1, hy - 9, hx - 1, hy - 13).stroke({ color: HELD_WHITE, width: 1.4, alpha: 0.6 });
      g.moveTo(hx + 3, hy - 4).bezierCurveTo(hx + 5, hy - 8, hx + 1, hy - 9, hx + 3, hy - 13).stroke({ color: HELD_WHITE, width: 1.4, alpha: 0.5 });
      break;
    }
    case 'pizza': {
      // A cheesy pizza slice (point down), with crust, sauce + a couple of pepperoni.
      g.poly([hx - 7, hy - 8, hx + 9, hy - 8, hx + 1, hy + 10]).fill(0xf2c14e); // cheese triangle
      g.poly([hx - 7, hy - 9.5, hx + 9, hy - 9.5, hx + 9, hy - 8, hx - 7, hy - 8]).fill(HELD_WOOD); // crust
      g.circle(hx - 1, hy - 4, 1.8).fill(0xd23f3f); // pepperoni
      g.circle(hx + 4, hy - 5, 1.6).fill(0xd23f3f);
      g.circle(hx + 1, hy + 1, 1.5).fill(0xd23f3f);
      break;
    }
    case 'bat': {
      // A baseball bat held up over the shoulder (a tapered barrel angling up-right) + a grip.
      g.poly([hx - 3, hy + 4, hx + 6, hy - 16, hx + 9, hy - 15, hx, hy + 5]).fill(HELD_WOOD); // barrel
      g.roundRect(hx - 4, hy + 2, 4, 5, 1.5).fill(0x5e3d22); // grip
      g.circle(hx + 7.5, hy - 15.5, 1.6).fill({ color: HELD_WHITE, alpha: 0.5 }); // tip highlight
      break;
    }
    case 'flyswatter': {
      // A flyswatter: a long handle + a perforated mesh head (up-right), with a tiny fly.
      g.moveTo(hx - 3, hy + 6).lineTo(hx + 6, hy - 12).stroke({ color: HELD_DARK, width: 2 }); // handle
      g.roundRect(hx + 3, hy - 20, 10, 11, 3).fill({ color: 0xff5d8f, alpha: 0.9 }); // mesh head
      for (const ox of [-2.5, 1, 4.5]) {
        for (const oy of [-17, -14, -11]) g.circle(hx + 6 + ox, hy + oy, 0.7).fill(0xb83968); // holes
      }
      g.circle(hx + 12, hy - 6, 1.4).fill(HELD_DARK); // a cheeky fly
      break;
    }
    case 'clipboard': {
      // A clipboard with a paper + a clip + a few ruled lines. Very official.
      g.roundRect(hx - 6, hy - 9, 16, 20, 1.5).fill(HELD_WOOD); // board
      g.roundRect(hx - 4.5, hy - 7, 13, 16, 1).fill(HELD_WHITE); // paper
      g.roundRect(hx - 3, hy - 11, 7, 3, 1).fill(0x6b7382); // clip
      for (const oy of [-3, 0, 3, 6]) g.moveTo(hx - 3, hy + oy).lineTo(hx + 7, hy + oy).stroke({ color: 0xc9ccd4, width: 0.8 });
      break;
    }
    case 'phone': {
      // A smartphone held up: a dark body + a bright screen + a little app dot grid.
      g.roundRect(hx - 4, hy - 11, 11, 20, 2.5).fill(HELD_DARK);
      g.roundRect(hx - 2.5, hy - 9, 8, 15, 1).fill(0x5ad1ff);
      for (const ox of [-1, 1.5, 4]) {
        for (const oy of [-6, -3]) g.circle(hx + ox, hy + oy, 0.8).fill({ color: HELD_WHITE, alpha: 0.8 });
      }
      g.circle(hx + 1.5, hy + 7, 1).fill(0x3a3f4a); // home dot
      break;
    }
    case 'balloon': {
      // A bright balloon bobbing on a string above the hand.
      g.moveTo(hx, hy + 6).bezierCurveTo(hx + 2, hy - 2, hx - 2, hy - 8, hx, hy - 14).stroke({ color: HELD_DARK, width: 0.8 }); // string
      g.ellipse(hx, hy - 22, 9, 11).fill(0xff5d8f);
      g.ellipse(hx - 3, hy - 25, 3, 4).fill({ color: HELD_WHITE, alpha: 0.5 }); // sheen
      g.poly([hx - 1.5, hy - 12, hx + 1.5, hy - 12, hx, hy - 9]).fill(0xff5d8f); // knot
      break;
    }
    case 'mug': {
      // A frothy beer mug: amber body, white foam head, a handle. Cheers!
      g.roundRect(hx - 5, hy - 6, 12, 16, 2).fill({ color: 0xf5a623, alpha: 0.92 });
      g.roundRect(hx - 5, hy - 9, 12, 5, 2).fill(HELD_WHITE); // foam
      g.circle(hx - 3, hy - 9, 2).fill(HELD_WHITE);
      g.circle(hx + 1, hy - 10, 2.2).fill(HELD_WHITE);
      g.circle(hx + 5, hy - 9, 2).fill(HELD_WHITE);
      g.circle(hx + 9, hy + 2, 3.5).stroke({ color: 0xd98a00, width: 2 }); // handle
      g.roundRect(hx - 3, hy - 4, 2, 12, 1).fill({ color: HELD_WHITE, alpha: 0.25 }); // glass sheen
      break;
    }
    default:
      break;
  }
  return g;
}
